import React from 'react';
import { Terminal, Code2, Check, XCircle, Blocks, Network, FileCode } from 'lucide-react';

export function TheProblemVisual() {
  return (
    <div className="relative w-full h-[360px] flex items-center justify-center isolate">
      {/* Subtle neutral background glow */}
      <div className="absolute inset-0 bg-white/[0.015] blur-[100px] rounded-full pointer-events-none" />
      
      {/* Central choked window */}
      <div className="relative z-10 w-[380px] h-[240px] bg-[#09090b]/90 backdrop-blur-2xl border border-white/10 rounded-xl shadow-[0_24px_60px_rgba(0,0,0,0.8)] flex flex-col overflow-hidden animate-float-slow">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-white/[0.06] bg-[#09090b]">
           <div className="flex gap-1.5 flex-1">
             <div className="w-2.5 h-2.5 rounded-full bg-white/10" />
             <div className="w-2.5 h-2.5 rounded-full bg-white/10" />
             <div className="w-2.5 h-2.5 rounded-full bg-white/10" />
           </div>
           <div className="text-[11px] font-mono text-white/40 truncate flex-[2] text-center tracking-wide">monolith-environment</div>
           <div className="flex-1" />
        </div>
        
        {/* Body */}
        <div className="p-4 flex flex-col gap-3 flex-1 relative bg-gradient-to-b from-transparent to-white/[0.005]">
           <div className="flex justify-between items-center text-[11px] text-white/30 font-mono mb-2">
             <div className="flex items-center gap-2">
               <span className="text-white/20">~</span> <span>main branch</span>
             </div>
           </div>
           
           {/* Minimal Code lines */}
           <div className="flex flex-col gap-2.5 opacity-40">
             <div className="h-[3px] w-full bg-white/20 rounded-full" />
             <div className="h-[3px] w-full bg-white/20 rounded-full" />
             <div className="h-[3px] w-5/6 bg-white/20 rounded-full" />
             <div className="h-[3px] w-2/3 bg-white/20 rounded-full" />
           </div>

           {/* Interruption floating card */}
           <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[80%] bg-[#08090a]/95 backdrop-blur-xl border border-white/10 p-4 rounded-xl shadow-[0_24px_50px_rgba(0,0,0,0.9)] animate-fade-up">
             <div className="flex items-start gap-4">
               <div className="w-7 h-7 rounded bg-white/5 flex items-center justify-center shrink-0 border border-white/10">
                 <XCircle size={14} className="text-white/60" />
               </div>
               <div>
                 <div className="text-[12px] text-white/90 font-medium mb-1 tracking-tight">Context Limit Reached</div>
                 <div className="text-[11px] text-white/40 leading-[1.6]">The agent's memory window is full. Stop the current task to start another.</div>
               </div>
             </div>
           </div>
        </div>
      </div>

      {/* Background windows representing clutter */}
      <div className="absolute z-0 w-[340px] h-[220px] bg-[#09090b]/50 backdrop-blur-md border border-white/[0.05] rounded-xl flex flex-col overflow-hidden animate-float-fast" style={{ transform: 'rotate(-3deg) translate(-25px, 20px)' }}>
         <div className="h-9 border-b border-white/[0.05] bg-[#09090b]/50" />
      </div>
      <div className="absolute z-[1] w-[300px] h-[220px] bg-[#09090b]/40 backdrop-blur-sm border border-white/[0.03] rounded-xl flex flex-col overflow-hidden animate-float-slow" style={{ transform: 'rotate(4deg) translate(30px, 30px)' }}>
         <div className="h-9 border-b border-white/[0.03] bg-[#09090b]/40" />
      </div>
    </div>
  )
}

export function TheSolutionVisual() {
  return (
    <div className="relative w-full h-[400px] flex items-center justify-center isolate" style={{ perspective: '1200px' }}>
       {/* Sleek, minimal background glow */}
       <div className="absolute inset-0 bg-white/[0.015] blur-[120px] rounded-full pointer-events-none" />

       {/* Window 1: Background Agent */}
       <div className="absolute w-[240px] h-[160px] bg-[#09090b]/90 backdrop-blur-xl border border-white/10 rounded-xl shadow-[0_20px_40px_rgba(0,0,0,0.6)] flex flex-col overflow-hidden animate-float-slow" style={{ transform: 'rotateY(-10deg) rotateX(5deg) translateZ(-80px) translateX(-140px) translateY(-10px)'}}>
          <div className="px-3 py-2.5 border-b border-white/[0.06] bg-[#09090b] flex items-center justify-between">
             <div className="flex items-center gap-2">
               <Blocks size={12} className="text-white/40" />
               <span className="text-[11px] font-mono text-white/60">agent-1.ts</span>
             </div>
             <div className="w-1.5 h-1.5 rounded-full bg-white/20" />
          </div>
          <div className="p-4 flex flex-col gap-2.5">
             <div className="h-[3px] w-full bg-white/10 rounded-full" />
             <div className="h-[3px] w-3/4 bg-white/10 rounded-full" />
             <div className="h-[3px] w-5/6 bg-white/10 rounded-full" />
          </div>
       </div>

       {/* Window 2: Primary Focus */}
       <div className="absolute w-[320px] h-[220px] bg-[#0d0e12]/95 backdrop-blur-2xl border border-white/[0.12] rounded-xl shadow-[0_30px_60px_rgba(0,0,0,0.8)] flex flex-col overflow-hidden animate-float-fast z-10" style={{ transform: 'translateZ(40px) translateY(20px)'}}>
          <div className="px-4 py-3 border-b border-white/[0.06] bg-[#0a0b0d] flex items-center justify-between">
             <div className="flex items-center gap-2">
               <FileCode size={13} className="text-white/60" />
               <span className="text-[11px] font-mono text-white/80">workspace/core</span>
             </div>
             <div className="flex gap-2 items-center">
                <span className="text-[10px] text-white/50 font-mono tracking-wide">Executing</span>
                <div className="w-1 h-1 rounded-full bg-white/60 animate-pulse" />
             </div>
          </div>
          <div className="p-5 flex flex-col flex-1">
             <div className="flex flex-col gap-3 flex-1">
               <div className="h-[3px] w-full bg-white/10 rounded-full" />
               <div className="h-[3px] w-5/6 bg-white/10 rounded-full" />
               <div className="h-[3px] w-4/6 bg-white/10 rounded-full" />
             </div>
             <div className="mt-auto flex items-center gap-2 text-[11px] font-mono text-white/50 border-t border-white/[0.03] pt-4">
               <Terminal size={12} className="text-white/30" />
               <div className="w-max whitespace-nowrap overflow-hidden animate-[typing_2s_steps(20,end)_infinite] border-r border-white/50 pr-1 text-white/70">delegating sub-task...</div>
             </div>
          </div>
       </div>

       {/* Window 3: Background Agent */}
       <div className="absolute w-[220px] h-[150px] bg-[#09090b]/90 backdrop-blur-xl border border-white/10 rounded-xl shadow-[0_20px_40px_rgba(0,0,0,0.6)] flex flex-col overflow-hidden" style={{ transform: 'rotateY(10deg) rotateX(-5deg) translateZ(-60px) translateX(150px) translateY(-30px)', animation: 'float-slow 7s infinite ease-in-out' }}>
          <div className="px-3 py-2.5 border-b border-white/[0.06] bg-[#09090b] flex items-center justify-between">
             <div className="flex items-center gap-2">
               <Network size={12} className="text-white/40" />
               <span className="text-[11px] font-mono text-white/60">agent-2.ts</span>
             </div>
             <div className="w-4 h-4 rounded bg-white/5 border border-white/10 flex items-center justify-center">
               <Check size={8} className="text-white/40" />
             </div>
          </div>
          <div className="p-4 flex flex-col flex-1">
            <div className="text-[10px] font-mono text-white/30 mb-3 tracking-wide">Tests passed</div>
            <div className="flex flex-col gap-2 mt-auto">
              <div className="h-[3px] w-full bg-white/10 rounded-full" />
              <div className="h-[3px] w-1/2 bg-white/10 rounded-full" />
            </div>
          </div>
       </div>
    </div>
  )
}
